import * as yup from "yup";
import React, { FC, useState } from "react";
import { Block } from "kanopi-components/Block";
import { Button, TButtonProps } from "kanopi-components/Button";
import { CardBody } from "kanopi-components/Card";
import { Form } from "kanopi-components/Form";
import { FormControl, Input } from "kanopi-components/Input";
import { HeadingSmall } from "kanopi-components/typography";
import { Modal } from "kanopi-components/Modal";
import { toaster } from "kanopi-components/ToastContainer";
import { useCustomers } from "../hooks/useCustomers";
import { useForm } from "react-weaver";
import { useNavigate } from "@reach/router";
import { handleErrorDisplay } from "kw/util/utils";

type TAdminCreateLazyUserModal = {
  openModal: (isOpen: boolean) => void;
  callBack?: (lazyUser: TUser) => void;
};

type TAdminCreateLazyUserButton = TButtonProps & {
  callBack?: (lazyUser: TUser) => void;
  buttonText?: string;
};

export const AdminCreateLazyUserModal: FC<TAdminCreateLazyUserModal> = ({
  openModal,
  callBack,
}: {
  openModal: (isOpen: boolean) => void;
  callBack?: (lazyUser: TUser) => void;
}) => {
  const navigate = useNavigate();
  const { loading, createLazyUser } = useCustomers();

  const { fieldProps, errors, values, notReady } = useForm({
    validator: yup.object().shape({
      email: yup.string().email().required().label("Email address"),
      firstName: yup.string().required().label("First name"),
      lastName: yup.string().required().label("Last name"),
    }),
  });

  const defaultCallback = (lazyUser: TUser) => {
    toaster({
      kind: "POSITIVE",
      content: `Customer ${lazyUser.firstName} ${lazyUser.lastName} has been created successfully.`,
      key: "create-lazy-user-success-toast",
    });
    navigate(`/admin/customers/${lazyUser.id}`, { replace: true });
  };

  const handleSubmit = async () => {
    try {
      const lazyUser = await createLazyUser(
        values.email,
        values.firstName,
        values.lastName
      );

      openModal(false);

      if (callBack) {
        callBack(lazyUser);
      } else {
        defaultCallback(lazyUser);
      }
    } catch (error) {
      handleErrorDisplay(error);
    }
  };

  return (
    <Modal onClose={() => openModal(false)} closeable isOpen={true} animate autoFocus>
      <Modal.Body $style={{ marginTop: "64px" }}>
        <Form>
          <CardBody
            display="grid"
            gridGap="scale900"
            gridTemplateColumns={["1fr"]}
            justifyItems="center"
            paddingBottom="scale900"
            paddingTop="scale900"
          >
            <Block>
              <HeadingSmall margin={0}>Add a new customer</HeadingSmall>
            </Block>

            <Block width="100%">
              <FormControl label="Email address" error={errors?.email}>
                <Input {...fieldProps.email} />
              </FormControl>
            </Block>

            <Block width="100%">
              <FormControl label="First Name" error={errors?.firstName}>
                <Input {...fieldProps.firstName} />
              </FormControl>
            </Block>

            <Block width="100%">
              <FormControl label="Last Name" error={errors?.lastName}>
                <Input {...fieldProps.lastName} />
              </FormControl>
            </Block>

            <Button
              type="submit"
              onClick={handleSubmit}
              isLoading={loading}
              disabled={loading || notReady}
            >
              Create
            </Button>
          </CardBody>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export const AdminCreateLazyUserButton = (props: TAdminCreateLazyUserButton) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { callBack, buttonText, ...rest } = props;
  const openModal = (isOpen: boolean) => {
    setIsModalOpen(isOpen);
  };

  return (
    <>
      <Button onClick={() => openModal(true)} outlined={true} {...rest}>
        {buttonText || "Add customers"}
      </Button>
      {isModalOpen && (
        <AdminCreateLazyUserModal openModal={openModal} callBack={callBack} />
      )}
    </>
  );
};
